// ============================================================================
// OFFICE (LOCATION) MASTER SCREEN
// ============================================================================
// Master CRUD screen for prima.basic.tbl_office via prima-api
// /api/v1/Offices endpoint.

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  MinimizedModalStrip,
  ColumnSelector,
  type ColumnDefinition,
} from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import {
  useOffices,
  useCreateOffice,
  useUpdateOffice,
  useDeleteOffice,
} from 'hooks/api/useOffice';
import { useCountries } from 'hooks/api/useCountry';
import { useStateProvinces } from 'hooks/api/useStateProvince';
import { useTimezones } from 'hooks/api/useTimezone';
import type {
  Office,
  OfficeSearchRequest,
  CreateOfficeRequest,
  UpdateOfficeRequest,
} from 'api/types';
import OfficeFormModal, {
  type OfficeFormData,
} from 'components/modals/modules/office/OfficeFormModal';

type ViewMode = 'list' | 'grid';

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ACTIVE', label: 'Active only' },
  { value: 'INACTIVE', label: 'Inactive only' },
];

const formatDate = (iso?: string | null): string => {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '-';
  }
};

interface OfficeMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const columnDefinitions: ColumnDefinition[] = [
  { id: 'code',      label: 'Code',     field: 'code',      required: true,  defaultVisible: true,  sortable: true },
  { id: 'name',      label: 'Location',  field: 'name',      required: true,  defaultVisible: true,  sortable: true },
  { id: 'cityName',  label: 'City',      field: 'cityName',  required: false, defaultVisible: true,  sortable: true },
  { id: 'stateName', label: 'State',     field: 'stateName', required: false, defaultVisible: true,  sortable: true },
  { id: 'status',    label: 'Status',    field: 'statusName', required: false, defaultVisible: true,  sortable: true },
  { id: 'createdAt', label: 'Created',   field: 'createdAt', required: false, defaultVisible: false, sortable: true },
  { id: 'updatedAt', label: 'Updated',   field: 'updatedAt', required: false, defaultVisible: false, sortable: true },
];

const COLUMN_PREF_KEY = 'office-master-columns';

const OfficeMasterScreen: React.FC<OfficeMasterScreenProps> = ({
  moduleIcon,
  tabId,
  moduleName,
  allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // ----------------------------------------------------------------------------
  // BMS Lookups for the form modal (country, state, timezone)
  // ----------------------------------------------------------------------------
  const { data: countries } = useCountries();
  const [selectedCountryId, setSelectedCountryId] = useState<string>('');
  const { data: states } = useStateProvinces(selectedCountryId || null);
  const { data: timezones } = useTimezones();

  const countryOptions = useMemo(
    () => countries.map((c) => ({ value: c.id, label: `${c.countryCode} - ${c.countryName}` })),
    [countries]
  );

  const stateOptions = useMemo(
    () => states.map((s) => ({ value: s.id, label: `${s.provinceCode} - ${s.provinceName}` })),
    [states]
  );

  const timezoneOptions = useMemo(
    () => timezones.map((tz) => ({ value: tz.tzCode, label: `${tz.utcOffsetDisplay || ''} ${tz.tzName}`.trim() })),
    [timezones]
  );

  const handleCountryChange = useCallback((countryId: string) => {
    setSelectedCountryId(countryId);
  }, []);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() =>
    columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id)
  );

  useEffect(() => {
    const saved = localStorage.getItem(COLUMN_PREF_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setVisibleColumns(parsed);
      } catch { /* ignore */ }
    }
  }, []);

  const handleApplyColumns = useCallback((cols: string[]) => {
    setVisibleColumns(cols);
    localStorage.setItem(COLUMN_PREF_KEY, JSON.stringify(cols));
  }, []);

  const handleResetColumns = useCallback(() => {
    const defaults = columnDefinitions.filter((c) => c.defaultVisible).map((c) => c.id);
    setVisibleColumns(defaults);
    localStorage.removeItem(COLUMN_PREF_KEY);
  }, []);

  // Server-side query state
  const [queryParams, setQueryParams] = useState<OfficeSearchRequest>({
    page: 1,
    pageSize: 25,
    includeInactive: true,
  });
  const { data, pagination, loading, refetch } = useOffices(queryParams);

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [codeFilter, setCodeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => ({
        ...prev,
        searchTerm: searchQuery || undefined,
        page: 1,
      }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Pagination
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // Modal state
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedRecord, setSelectedRecord] = useState<Office | null>(null);
  const [isSavingForm, setIsSavingForm] = useState(false);
  const [formApiError, setFormApiError] = useState<string | null>(null);

  const createInstanceCounter = useRef(0);
  const [createInstanceId, setCreateInstanceId] = useState<string>('0');
  const formModalMinimizeRef = useRef<(() => void) | null>(null);

  const handleRestoreModal = useCallback(
    (restoredMode: 'create' | 'edit', instanceId?: string, recordCode?: string) => {
      if (instanceId) setCreateInstanceId(instanceId);
      if (recordCode) {
        const rec = data.find((r) => r.code === recordCode);
        if (rec) setSelectedRecord(rec);
      }
      setFormMode(restoredMode);
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [data]
  );

  // Delete state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Office | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mutation hooks
  const { createOffice } = useCreateOffice();
  const { updateOffice } = useUpdateOffice();
  const { deleteOffice } = useDeleteOffice();

  // Column definitions
  const tableColumns: SyutGridColumn<Office>[] = useMemo(
    () => [
      { id: 'code', header: 'Code', accessorKey: 'code', size: 120, enableSorting: true },
      { id: 'name', header: 'Location', accessorKey: 'name', enableSorting: true },
      {
        id: 'cityName',
        header: 'City',
        accessorKey: 'cityName',
        size: 120,
        enableSorting: true,
        cell: ({ row }) => row.original.cityName || '-',
      },
      {
        id: 'stateName',
        header: 'State',
        accessorKey: 'stateName',
        size: 120,
        enableSorting: true,
        cell: ({ row }) => row.original.stateName || '-',
      },
      {
        id: 'status',
        header: 'Status',
        accessorKey: 'statusName',
        size: 110,
        enableSorting: true,
        cell: ({ row }) => {
          const isActive = row.original.statusCode === 'ACTIVE';
          return (
            <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
              {row.original.statusName || (isActive ? 'Active' : 'Inactive')}
            </span>
          );
        },
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessorKey: 'createdAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.createdAt),
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessorKey: 'updatedAt',
        size: 130,
        enableSorting: true,
        cell: ({ row }) => formatDate(row.original.updatedAt),
      },
    ],
    []
  );

  const visibleTableColumns = useMemo<SyutGridColumn<Office>[]>(
    () => tableColumns.filter((col) => visibleColumns.includes(col.id as string)),
    [tableColumns, visibleColumns]
  );

  // Handlers
  const handleAddNew = useCallback(() => {
    const newId = String(++createInstanceCounter.current);
    setCreateInstanceId(newId);
    setSelectedRecord(null);
    setFormMode('create');
    setFormApiError(null);
    setFormModalOpen(true);
  }, []);

  const handleEdit = useCallback(
    (record: Office) => {
      const modalId = tabId ? `${tabId}-form-modal-edit-${record.code}` : null;
      if (modalId && isMinimized(modalId)) {
        const modal = getMinimizedModal(modalId);
        dismissMinimized(modalId);
        modal?.onRestore?.();
        return;
      }
      setSelectedRecord(record);
      setFormMode('edit');
      setFormApiError(null);
      setFormModalOpen(true);
    },
    [tabId, isMinimized, dismissMinimized, getMinimizedModal]
  );

  const handleDeleteClick = useCallback((record: Office) => {
    setDeleteTarget(record);
    setDeleteModalOpen(true);
  }, []);

  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Office | null>(null);

  const handleView = useCallback((record: Office) => {
    setViewRecord(record);
    setViewModalOpen(true);
  }, []);

  // Save handler
  const handleSaveForm = useCallback(
    async (formData: OfficeFormData) => {
      setIsSavingForm(true);
      setFormApiError(null);
      try {
        if (formMode === 'create') {
          const payload: CreateOfficeRequest = {
            code: formData.code,
            name: formData.name,
            aliasName: formData.aliasName || undefined,
            address: formData.address || undefined,
            cityName: formData.cityName || undefined,
            countryId: formData.countryId || undefined,
            countryName: formData.countryName || undefined,
            stateId: formData.stateId || undefined,
            stateName: formData.stateName || undefined,
            postalCode: formData.postalCode || undefined,
            phone: formData.phone || undefined,
            email: formData.email || undefined,
            timeZone: formData.timeZone || undefined,
            description: formData.description || undefined,
            statusCode: formData.statusCode,
          };
          await createOffice(payload);
          notificationService.toast.success(`Location "${payload.code}" created.`);
        } else if (selectedRecord) {
          const payload: UpdateOfficeRequest = {
            code: formData.code,
            name: formData.name,
            aliasName: formData.aliasName || undefined,
            address: formData.address || undefined,
            cityName: formData.cityName || undefined,
            countryId: formData.countryId || undefined,
            countryName: formData.countryName || undefined,
            stateId: formData.stateId || undefined,
            stateName: formData.stateName || undefined,
            postalCode: formData.postalCode || undefined,
            phone: formData.phone || undefined,
            email: formData.email || undefined,
            timeZone: formData.timeZone || undefined,
            description: formData.description || undefined,
            statusCode: formData.statusCode,
            rowVersion: selectedRecord.rowVersion,
          };
          await updateOffice(selectedRecord.id, payload);
          notificationService.toast.success(`Location "${payload.code}" updated.`);
        }
        setIsSavingForm(false);
        setFormModalOpen(false);
        setSelectedRecord(null);
        refetch();
      } catch (err: any) {
        setIsSavingForm(false);
        const apiBody = err?.response?.data;
        let message = 'Failed to save location. Please try again.';
        if (typeof apiBody?.message === 'string' && apiBody.message) {
          message = apiBody.message;
        } else if (Array.isArray(apiBody?.errors) && apiBody.errors.length) {
          message = apiBody.errors.join(', ');
        } else if (apiBody?.errors && typeof apiBody.errors === 'object') {
          message = Object.values(apiBody.errors).flat().join(', ');
        } else if (err?.message) {
          message = err.message;
        }
        setFormApiError(message);
        notificationService.toast.error(message, 6000);
      }
    },
    [formMode, selectedRecord, createOffice, updateOffice, refetch]
  );

  // Delete handler
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await deleteOffice(deleteTarget.id);
      notificationService.toast.success(`Location "${deleteTarget.code}" deleted.`);
      setIsDeleting(false);
      setDeleteModalOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      const apiBody = err?.response?.data;
      const message =
        (typeof apiBody?.message === 'string' && apiBody.message) ||
        err?.message ||
        'Failed to delete location.';
      notificationService.toast.error(message, 6000);
    }
  }, [deleteTarget, deleteOffice, refetch]);

  // Active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (queryParams.code) count++;
    if (queryParams.name) count++;
    if (queryParams.statusCode) count++;
    return count;
  }, [queryParams.code, queryParams.name, queryParams.statusCode]);

  // Filter Apply / Clear
  const applyFilters = useCallback(() => {
    setQueryParams((prev) => ({
      ...prev,
      code: codeFilter || undefined,
      name: nameFilter || undefined,
      statusCode: statusFilter || undefined,
      includeInactive: true,
      page: 1,
    }));
    setFilterOpen(false);
  }, [codeFilter, nameFilter, statusFilter]);

  const clearFilters = useCallback(() => {
    setCodeFilter('');
    setNameFilter('');
    setStatusFilter('');
    setQueryParams((prev) => ({
      page: 1,
      pageSize: prev.pageSize,
      includeInactive: true,
      statusCode: undefined,
      searchTerm: searchQuery || undefined,
    }));
  }, [searchQuery]);

  // Row actions
  const getRowActions = useCallback(
    (row: Office): ActionMenuItem[] => {
      const items: ActionMenuItem[] = [];
      if (allowedActions.includes('view')) {
        items.push({
          id: 'view',
          label: 'View',
          icon: <IconSystem.eye size={16} />,
          onClick: () => handleView(row),
        });
      }
      if (allowedActions.includes('edit')) {
        items.push({
          id: 'edit',
          label: 'Edit',
          icon: <IconSystem.edit size={16} />,
          onClick: () => handleEdit(row),
        });
      }
      if (allowedActions.includes('delete')) {
        items.push({
          id: 'delete',
          label: 'Delete',
          icon: <IconSystem.trash size={16} />,
          onClick: () => handleDeleteClick(row),
          variant: 'danger',
        });
      }
      return items;
    },
    [allowedActions, handleView, handleEdit, handleDeleteClick]
  );

  // Action buttons
  const actionButtons: ActionButton[] = useMemo(
    () => [
      ...(allowedActions.includes('add') ? [{
        id: 'add',
        icon: <IconSystem.add size={18} />,
        label: 'Add',
        tooltip: 'Add New Location',
        onClick: handleAddNew,
        variant: 'icon' as const,
      }] : []),
      {
        id: 'refresh',
        icon: <IconSystem.refresh size={18} />,
        label: 'Refresh',
        tooltip: 'Refresh',
        onClick: () => refetch(),
        variant: 'icon' as const,
      },
    ],
    [allowedActions, handleAddNew, refetch]
  );

  // Form initial data
  const formInitialData: OfficeFormData | null = useMemo(() => {
    if (formMode !== 'edit' || !selectedRecord) return null;
    // Pre-load states for the selected record's country
    if (selectedRecord.countryId) setSelectedCountryId(selectedRecord.countryId);
    return {
      code: selectedRecord.code,
      name: selectedRecord.name,
      aliasName: selectedRecord.aliasName ?? '',
      address: selectedRecord.address ?? '',
      cityName: selectedRecord.cityName ?? '',
      countryId: selectedRecord.countryId ?? '',
      countryName: selectedRecord.countryName ?? '',
      stateId: selectedRecord.stateId ?? '',
      stateName: selectedRecord.stateName ?? '',
      postalCode: selectedRecord.postalCode ?? '',
      phone: selectedRecord.phone ?? '',
      email: selectedRecord.email ?? '',
      timeZone: selectedRecord.timeZone ?? '',
      description: selectedRecord.description ?? '',
      statusCode: (selectedRecord.statusCode as 'ACTIVE' | 'INACTIVE') ?? 'ACTIVE',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formMode, selectedRecord]);

  // Render
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Location Master"
        description="Manage office locations for region-specific holidays"
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.home size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search code, name, city…',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: [
            { id: 'list', icon: <IconSystem.list size={16} />, label: 'List', tooltip: 'List view' },
            { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid', tooltip: 'Grid view' },
          ],
          onChange: (mode) => setViewMode(mode as ViewMode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        customToolbarItems={
          <ColumnSelector
            columns={columnDefinitions}
            visibleColumns={visibleColumns}
            onApply={handleApplyColumns}
            onReset={handleResetColumns}
          />
        }
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {viewMode === 'list' && (
              <SyutGrid
                data={data}
                columns={visibleTableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? 25}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableSorting
                rowActions={
                  (allowedActions.includes('view') || allowedActions.includes('edit') || allowedActions.includes('delete'))
                    ? getRowActions
                    : undefined
                }
                emptyMessage={loading ? 'Loading locations…' : 'No locations found'}
              />
            )}

            {viewMode === 'grid' && (
              <div
                className="syut-grid-component"
                style={{ height: '100%', display: 'flex', flexDirection: 'column' }}
              >
                <div
                  style={{
                    padding: '1rem',
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1.5rem',
                    flex: 1,
                    overflow: 'auto',
                    alignContent: 'start',
                  }}
                >
                  {data.length === 0 && (
                    <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                      {loading ? 'Loading locations…' : 'No locations found'}
                    </div>
                  )}
                  {data.map((item) => {
                    const isActive = item.statusCode === 'ACTIVE';
                    return (
                      <div
                        key={String(item.id)}
                        style={{
                          background: 'var(--bg-primary)',
                          border: '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '1rem',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '0.5rem',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.25rem' }}>
                              {item.code}
                            </div>
                            <h3 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, marginBottom: '0.5rem' }}>
                              {item.name}
                            </h3>
                          </div>
                          <ActionMenu items={getRowActions(item)} />
                        </div>
                        {(item.cityName || item.stateName) && (
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                            {[item.cityName, item.stateName].filter(Boolean).join(', ')}
                          </div>
                        )}
                        <div style={{ marginTop: 'auto' }}>
                          <span className={`badge badge-${isActive ? 'active' : 'inactive'}`}>
                            {item.statusName || (isActive ? 'Active' : 'Inactive')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {filterOpen && (
        <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Code</label>
              <input type="text" className="form-input" value={codeFilter} onChange={(e) => setCodeFilter(e.target.value.toUpperCase())}
                placeholder="Filter by code…" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Location</label>
              <input type="text" className="form-input" value={nameFilter} onChange={(e) => setNameFilter(e.target.value)}
                placeholder="Filter by name…" style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
            </div>
            <div className="filter-section">
              <SyutSelect label="Status" options={STATUS_FILTER_OPTIONS} value={statusFilter}
                onChange={(val) => setStatusFilter((val as string) || '')} isClearable={false} size="sm" />
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={clearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={applyFilters}>Apply</button>
          </div>
        </div>
        )}
      </div>

      <OfficeFormModal
        isOpen={formModalOpen}
        mode={formMode}
        initialData={formInitialData}
        isSaving={isSavingForm}
        apiError={formApiError}
        onSave={handleSaveForm}
        onClose={() => {
          setFormModalOpen(false);
          setSelectedRecord(null);
          setFormApiError(null);
        }}
        countryOptions={countryOptions}
        stateOptions={stateOptions}
        timezoneOptions={timezoneOptions}
        onCountryChange={handleCountryChange}
        tabId={tabId}
        moduleName={moduleName}
        onRestoreModal={handleRestoreModal}
        createInstanceId={createInstanceId}
        minimizeRef={formModalMinimizeRef}
      />

      <ConfirmDialog
        open={deleteModalOpen}
        title="Delete Location"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.code} — ${deleteTarget.name}"? This action cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => {
          if (!isDeleting) {
            setDeleteModalOpen(false);
            setDeleteTarget(null);
          }
        }}
      />

      {viewModalOpen && viewRecord && (
        <>
          <div className="modal-backdrop" />
          <div className="modal-container modal-md">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">View Location — {viewRecord.code}</h2>
              <button className="modal-close" onClick={() => { setViewModalOpen(false); setViewRecord(null); }} title="Close">
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content" style={{ fontSize: '0.8125rem' }}>
              <div className="form-grid-2">
                <div>
                  <label className="form-label">Code</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.code || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Location</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.name || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Address</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.address || '—'}</div>
                </div>
                <div>
                  <label className="form-label">City</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.cityName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">State</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.stateName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Country</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.countryName || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Postal Code</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.postalCode || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Phone</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.phone || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Email</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.email || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Timezone</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>{viewRecord.timeZone || '—'}</div>
                </div>
                <div>
                  <label className="form-label">Status</label>
                  <div style={{ padding: '0.4rem 0.5rem', background: 'var(--bg-secondary)', borderRadius: '0.375rem', border: '1px solid var(--border-color)' }}>
                    <span className={`badge badge-${viewRecord.statusCode === 'ACTIVE' ? 'active' : 'inactive'}`}>{viewRecord.statusName}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button className="btn btn-secondary" onClick={() => { setViewModalOpen(false); setViewRecord(null); }}>Close</button>
              {allowedActions.includes('edit') && (
                <button className="btn btn-primary" onClick={() => { setViewModalOpen(false); setViewRecord(null); handleEdit(viewRecord); }}>Edit</button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default OfficeMasterScreen;
